/**
 * Shopping Feature API
 */

import { apiFetch, apiFormFetch, buildUrl, PROXY_BASE, AI_TIMEOUT } from "@/shared/api/client";
import type { ApiResponse, Budget } from "@/shared/api/types";
import type {
  ShoppingHistoryParams,
  ShoppingHistoryResponse,
  ShoppingWardrobeRequest,
  ShoppingWardrobeResponse,
  ShoppingImageResponse,
  ShoppingWishlistItem,
  ShoppingWishlistResponse,
  AddToShoppingWishlistRequest,
  ToggleShoppingWishlistResponse,
} from "./types";

/**
 * Get shopping suggestions history (paginated)
 */
export async function getShoppingHistory(
  params: ShoppingHistoryParams
): Promise<ApiResponse<ShoppingHistoryResponse>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/shopping`, params as unknown as Record<string, unknown>));
}

/**
 * Generate AI-powered shopping recommendations based on wardrobe analysis
 */
export async function generateWardrobeShopping(
  request: ShoppingWardrobeRequest
): Promise<ApiResponse<ShoppingWardrobeResponse>> {
  return apiFetch(`${PROXY_BASE}/shopping/wardrobe`, {
    method: "POST",
    body: JSON.stringify(request),
    timeout: AI_TIMEOUT,
  });
}

/**
 * Generate shopping recommendations from uploaded image
 * Cost: 10 Hoku Coins (Feature Code: HOKU004)
 */
export async function generateImageShopping(
  userId: string,
  image: File,
  options?: { budget?: Budget; preferredBrands?: string[]; occasion?: string }
): Promise<ApiResponse<ShoppingImageResponse>> {
  const formData = new FormData();
  formData.append("userId", userId);
  formData.append("image", image);
  if (options?.budget) formData.append("budget", options.budget);
  if (options?.preferredBrands) {
    formData.append("preferredBrands", JSON.stringify(options.preferredBrands));
  }
  if (options?.occasion) formData.append("occasion", options.occasion);

  return apiFormFetch(`${PROXY_BASE}/shopping/image`, formData);
}

/**
 * Get shopping wishlist items
 */
export async function getShoppingWishlist(
  params?: { limit?: number; offset?: number }
): Promise<ApiResponse<ShoppingWishlistResponse>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/shopping/wishlist`, params as Record<string, unknown>));
}

/**
 * Add item to shopping wishlist
 */
export async function addToShoppingWishlist(
  item: AddToShoppingWishlistRequest
): Promise<ApiResponse<ShoppingWishlistItem>> {
  return apiFetch(`${PROXY_BASE}/shopping/wishlist`, {
    method: "POST",
    body: JSON.stringify(item),
  });
}

/**
 * Toggle shopping wishlist status (add if not exists, remove if exists)
 */
export async function toggleShoppingWishlist(
  item: AddToShoppingWishlistRequest
): Promise<ApiResponse<ToggleShoppingWishlistResponse>> {
  return apiFetch(`${PROXY_BASE}/shopping/wishlist/toggle`, {
    method: "POST",
    body: JSON.stringify(item),
  });
}

/**
 * Remove item from shopping wishlist by ID
 */
export async function removeFromShoppingWishlist(
  id: string
): Promise<ApiResponse<{ message: string }>> {
  return apiFetch(`${PROXY_BASE}/shopping/wishlist/${id}`, {
    method: "DELETE",
  });
}
